import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/meet-jeremy", label: "Meet Jeremy" },
  { to: "/how-solar-works", label: "How Solar Works" },
  { to: "/why-solar-now", label: "Why Solar Now" },
  { to: "/homeowner-questions", label: "Homeowner Questions" },
  { to: "/why-ion", label: "Why ION" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const hasDarkHero = location.pathname === "/";
  const lightMode = hasDarkHero && !scrolled;

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md bg-[#FDFBF7]/85 border-b border-[#E5E0D8]" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" aria-label="Jeremy with ION Solar — home" className="shrink-0">
          <Logo variant={lightMode ? "light" : "dark"} />
        </Link>

        <div className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => {
                if (lightMode) {
                  return `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "text-[#FEF3C7] bg-white/10" : "text-[#F8FAFC]/90 hover:text-[#FEF3C7] hover:bg-white/10"
                  }`;
                }
                return `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "text-[#0F382C] bg-[#E8F0EC]" : "text-slate-700 hover:text-[#0F382C] hover:bg-[#F7F4EB]"
                }`;
              }}
            >
              {n.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/qualify?upload=1"
            className="hidden md:inline-flex btn-solar text-xs sm:text-sm"
            data-testid="nav-review-bill-btn"
          >
            Have Jeremy Review My Bill <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            className={`xl:hidden p-2 rounded-full ${lightMode ? "text-[#FDFBF7] hover:bg-white/10" : "text-[#0F382C] hover:bg-[#F7F4EB]"}`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="xl:hidden bg-[#FDFBF7] border-t border-[#E5E0D8] px-4 py-4 space-y-1" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-xl text-base font-medium ${
                  isActive ? "bg-[#E8F0EC] text-[#0F382C]" : "text-slate-700"
                }`
              }
              data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
          <Link to="/qualify?upload=1" className="btn-solar w-full justify-center mt-3" data-testid="nav-mobile-review-btn">
            Have Jeremy Review My Bill <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </header>
  );
}
